import React, { useEffect, useState } from "react";
import { Bell, BellDot, ChevronUp, ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";

import { formatDate } from "../../utils/formatDateUtils";
import { getTaskManagerNotifications } from "../../store/slices/taskmanagementSlice";

interface TaskNotificationProps {
  className?: string;
}

const TaskNotification: React.FC<TaskNotificationProps> = ({ className }) => {
  const dispatch = useDispatch<RootDispatch>();
  const notifications = useSelector(
    (state: RootState) => state.TaskManagement.taskNotification,
  );
  const pagination = useSelector(
    (state: RootState) => state.TaskManagement.taskNotificationPagination,
  );

  const [loadingPage, setLoadingPage] = useState(false);

  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!notifications || notifications.length === 0) {
      dispatch(getTaskManagerNotifications({ page: 1, limit: 10 }));
    }
  }, [dispatch]);

  const goToPage = async (page: number) => {
    if (!pagination) return;
    if (page < 1 || page > pagination.totalPages) return;

    setLoadingPage(true);
    await dispatch(
      getTaskManagerNotifications({ page, limit: pagination.limit || 10 }),
    );
    setLoadingPage(false);
    setExpanded(null);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  const renderMetaInfo = (meta: any) => {
    if (!meta || !meta.metaType) return null;

    const infoRow = (label: string, value: any) =>
      value ? (
        <p className="flex justify-between text-xs text-gray-700 border-b border-gray-100 py-0.5">
          <span className="font-medium text-gray-800">{label}:</span>
          <span>{value}</span>
        </p>
      ) : null;

    switch (meta.metaType) {
      case "duplicate":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Remark", meta.remark)}
            {infoRow("Requested By", meta.updatedBy)}
            {infoRow("Want to Duplicate", meta.taskId)}
            {infoRow("Duplicate Of Task", meta.duplicateOfTaskId)}
          </div>
        );

      case "attachment":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Action", meta.action)}
            {infoRow("Updated By", meta.updatedBy)}
          </div>
        );
      case "quote_status_update":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Old Status", meta.oldStatus)}
            {infoRow("New Status", meta.newStatus)}
            {meta.remark?.trim() ? infoRow("Remark", meta.remark) : null}
          </div>
        );
      case "status":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Updated By", meta.updatedBy)}
            {infoRow("Old Status", meta.oldStatus)}
            {infoRow("New Status", meta.newStatus)}
            {infoRow("Decision", meta.decision)}
            {infoRow("Approx. Time", meta.approxTime)}
          </div>
        );

      case "updateTask":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Field Name", meta.field)}
            {infoRow(
              "Old Value",
              meta.field === "startDate" ||
                meta.field === "endDate" ||
                meta.field === "requestedEndDate"
                ? formatDate(meta.oldValue)
                : meta.field === "assignee"
                  ? meta.oldValue?.username
                  : Array.isArray(meta.oldValue) && meta.field === "followers"
                    ? meta.oldValue.map((f: any) => f.username).join(", ")
                    : meta.oldValue?.toString(),
            )}
            {infoRow(
              "New Value",
              meta.field === "startDate" ||
                meta.field === "endDate" ||
                meta.field === "requestedEndDate"
                ? formatDate(meta.newValue)
                : meta.field === "assignee"
                  ? meta.newValue?.username
                  : Array.isArray(meta.newValue) && meta.field === "followers"
                    ? meta.newValue.map((f: any) => f.username).join(", ")
                    : meta.newValue?.toString(),
            )}
            {infoRow("Updated By", meta.updatedBy)}
          </div>
        );

      case "assignee":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Old Assignee", meta.oldAssignee)}
            {infoRow("New Assignee", meta.newAssignee)}
            {infoRow("Updated By", meta.updatedBy)}
          </div>
        );

      case "comment":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm">
            {infoRow("Added By", meta.mentionedBy)}
          </div>
        );

      case "date-extension":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Decision", meta.decision)}
            {infoRow(
              "Requested End Date",
              meta.requestedEndDate ? formatDate(meta.requestedEndDate) : "-",
            )}
            {infoRow("Updated By", meta.updatedBy)}
          </div>
        );
      case "epc_document":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Uploaded By", meta.updatedBy)}
          </div>
        );
      case "new_task_assignment":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Assigned By", meta.assignedBy)}
          </div>
        );
      case "repayment_task":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
            {infoRow("Total Cost", meta.totalCost)}
            {infoRow("Assigned By", meta.assignedBy)}
          </div>
        );
      case "meter_file":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
          </div>
        );
      case "repayment_rejected":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
            {infoRow("Sales Rep Name", meta.salesRepName)}
            {infoRow("Repayment Amount", meta.repaymentAmount)}
          </div>
        );
      case "payment_approved":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
            {infoRow("Sales Rep Name", meta.salesRepName)}
            {infoRow("CSE Name", meta.cseName)}
          </div>
        );
      case "lead_payment":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
            {infoRow("Mobile Number", meta.mobileNumber)}
          </div>
        );
      case "lead_payment_approval":
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm space-y-1">
            {infoRow("Customer name", meta.customerName)}
          </div>
        );
      default:
        return (
          <div className="mt-2 border-t border-gray-200 pt-2 text-sm text-gray-600">
            No additional information available.
          </div>
        );
    }
  };

  return (
    <div
      className={`w-full bg-white shadow-md rounded-xl border border-gray-200 ${
        className ?? ""
      }`}
    >
      {notifications?.length === 0 ? (
        <div className="text-center text-gray-400 py-10">
          <Bell className="mx-auto mb-2" size={36} />
          No notifications
        </div>
      ) : (
        <>
          <ul className="divide-y divide-gray-200">
            {notifications.map((notif) => (
              <li key={notif._id}>
                <div
                  className={`flex items-start justify-between p-4 cursor-pointer transition-colors ${
                    notif.isRead ? "bg-white" : "bg-gray-50"
                  } hover:bg-gray-100`}
                  onClick={() => notif._id && toggleExpand(notif._id)}
                >
                  <div className="flex gap-3">
                    <div className="mt-1">
                      <BellDot className="text-blue-600" size={18} />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{notif.title}</p>
                      <p className="text-gray-500 text-sm">
                        {notif.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(notif.createdAt).toLocaleString([], {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </p>
                        {notif.link && (
                          <a
                            href={notif.link}
                            className="text-blue-600 text-lg font-medium hover:underline inline-block"
                          >
                            →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-400">
                    {expanded === notif._id ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </div>
                </div>

                {expanded === notif._id && (
                  <div className="px-6 pb-4 bg-gray-50 border-t border-gray-200">
                    {notif.meta && renderMetaInfo(notif.meta)}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {pagination && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
              <div className="text-sm text-gray-600">
                Page <span className="font-semibold">{pagination.page}</span> /{" "}
                <span className="font-semibold">{pagination.totalPages}</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loadingPage}
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page <= 1
                      ? "bg-gray-100 text-gray-400"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Prev
                </button>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={
                    pagination.page >= pagination.totalPages || loadingPage
                  }
                  className={`px-3 py-1 rounded text-sm ${
                    pagination.page >= pagination.totalPages
                      ? "bg-gray-100 text-gray-400"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskNotification;
